/**
 * Todo lo que el panel le pide a la API del Centro pasa por acá: misma sesión (cookie
 * httpOnly del mismo origen) y los errores en un solo formato, para mostrarlos tal cual.
 */
import { tx } from '../i18n';

/** Un error que se le puede mostrar a la persona: la API ya lo explica en inglés. */
export class ErrorDeApi extends Error {
  constructor(
    mensaje: string,
    /** 0 = no hubo respuesta (red o API caída). */
    readonly codigo: number,
    readonly detalles: string[] = [],
  ) {
    super(mensaje);
    this.name = 'ErrorDeApi';
  }
}

let alPerderSesionFn: (() => void) | null = null;
/** Qué hacer cuando la API dice que la sesión ya no vale (401): volver al ingreso. */
export function alPerderSesion(fn: (() => void) | null) {
  alPerderSesionFn = fn;
}

export interface Opciones {
  metodo?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  cuerpo?: unknown;
  consulta?: Record<string, string | number | boolean | undefined | null>;
  senal?: AbortSignal;
}

export async function pedir<T>(ruta: string, o: Opciones = {}): Promise<T> {
  const url = new URL(`/api${ruta}`, window.location.origin);
  for (const [k, v] of Object.entries(o.consulta ?? {})) if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));

  let r: Response;
  try {
    r = await fetch(url, {
      method: o.metodo ?? 'GET',
      headers: o.cuerpo !== undefined ? { 'Content-Type': 'application/json' } : {},
      body: o.cuerpo !== undefined ? JSON.stringify(o.cuerpo) : undefined,
      credentials: 'same-origin',
      signal: o.senal,
    });
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    throw new ErrorDeApi(tx.comun.errorRed, 0);
  }
  if (r.status === 204) return undefined as T;
  const datos = (await r.json().catch(() => null)) as { error?: string; detalles?: string[] } | null;
  if (!r.ok) {
    // El 401 del propio ingreso es "clave incorrecta", no una sesión vencida.
    if (r.status === 401 && ruta !== '/sesion') alPerderSesionFn?.();
    throw new ErrorDeApi(datos?.error ?? tx.comun.errorSinMotivo(r.status), r.status, datos?.detalles ?? []);
  }
  return datos as T;
}

/** El mensaje para mostrar, con los detalles de validación si los hay. */
export function mensajeDe(e: unknown): string {
  if (e instanceof ErrorDeApi) return e.detalles.length ? `${e.message}: ${e.detalles.join('; ')}` : e.message;
  return tx.comun.errorRaro;
}
